import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { useIsMobile } from "@/hooks/use-mobile";

interface PeriodFilterProps {
  selectedYear: number | null;
  selectedMonth: number | null;
  onYearChange: (year: number) => void;
  onMonthChange: (month: number) => void;
}

const currentYear = new Date().getFullYear();

// Static year range: current year ± 5 years
const AVAILABLE_YEARS = Array.from({ length: 11 }, (_, i) => currentYear + 5 - i);

// All 12 months always available
const MONTHS = [
  { value: 1, label: 'January', short: 'Jan' },
  { value: 2, label: 'February', short: 'Feb' },
  { value: 3, label: 'March', short: 'Mar' },
  { value: 4, label: 'April', short: 'Apr' },
  { value: 5, label: 'May', short: 'May' },
  { value: 6, label: 'June', short: 'Jun' },
  { value: 7, label: 'July', short: 'Jul' },
  { value: 8, label: 'August', short: 'Aug' },
  { value: 9, label: 'September', short: 'Sep' },
  { value: 10, label: 'October', short: 'Oct' },
  { value: 11, label: 'November', short: 'Nov' },
  { value: 12, label: 'December', short: 'Dec' },
];

export const PeriodFilter = ({ 
  selectedYear, 
  selectedMonth, 
  onYearChange, 
  onMonthChange 
}: PeriodFilterProps) => {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <div className="grid grid-cols-2 gap-2 w-full">
        <Select 
          value={selectedYear?.toString() || ""} 
          onValueChange={(value) => onYearChange(parseInt(value))}
        >
          <SelectTrigger className="h-12 w-full">
            <SelectValue placeholder="Year" />
          </SelectTrigger>
          <SelectContent>
            {AVAILABLE_YEARS.map((year) => (
              <SelectItem key={year} value={year.toString()}>
                {year}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select 
          value={selectedMonth?.toString() || ""} 
          onValueChange={(value) => onMonthChange(parseInt(value))}
        >
          <SelectTrigger className="h-12 w-full">
            <SelectValue placeholder="Month" />
          </SelectTrigger>
          <SelectContent className="max-h-[300px]">
            {MONTHS.map((month) => (
              <SelectItem key={month.value} value={month.value.toString()}>
                {month.short}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-4">
      <div className="flex items-center gap-2">
        <Label htmlFor="year" className="text-sm font-medium">Year</Label>
        <Select 
          value={selectedYear?.toString() || ""} 
          onValueChange={(value) => onYearChange(parseInt(value))}
        >
          <SelectTrigger id="year" className="w-[100px]">
            <SelectValue placeholder="Select year" />
          </SelectTrigger>
          <SelectContent>
            {AVAILABLE_YEARS.map((year) => (
              <SelectItem key={year} value={year.toString()}>
                {year}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center gap-2">
        <Label htmlFor="month" className="text-sm font-medium">Month</Label>
        <Select 
          value={selectedMonth?.toString() || ""} 
          onValueChange={(value) => onMonthChange(parseInt(value))}
        >
          <SelectTrigger id="month" className="w-[130px]">
            <SelectValue placeholder="Select month" />
          </SelectTrigger>
          <SelectContent className="max-h-[450px]">
            {MONTHS.map((month) => (
              <SelectItem key={month.value} value={month.value.toString()}>
                {month.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};
